import { Agency } from '@app/models';
import {
  AdvancedSearchActions,
  AdvancedSearchActionTypes,
  InitAdvancedSearchForm
} from '@app/pcs/actions/advanced-search';
import {
  Corridor,
  DictionaryEntity,
  DossierSearchCriteria,
  DossierSearchType,
  Label,
  ofSearchType,
  OperationPoint,
  ProcessType,
  ProgressStatus,
  TimetablePeriod,
  TrainType
} from '@app/pcs/models';
import { getAdvancedSearchAdditionalFields } from '@app/pcs/reducer/advanced-search/additional-fields';
import { requiredValueValidator } from '@app/shared/validators';
import { Action } from '@ngrx/store';
import {
  createHandler,
  createReducer,
  executeReducers,
  getControls,
  getControlValue,
  isObject,
  isTrue,
  safeProp
} from '@util';
import {
  addArrayControl,
  box,
  Boxed,
  createFormGroupState,
  disable,
  FormGroupState,
  formStateReducer,
  removeArrayControl,
  removeGroupControl,
  setUserDefinedProperty,
  setValue,
  unbox,
  updateArrayWithFilter,
  updateGroup,
  validate
} from 'ngrx-forms';
import * as R from 'ramda';

const ADVANCED_SEARCH_FORM_ID = 'AdvancedSearchForm';
export const PROGRESS_STATUS = 'progressStatus';
export const MY_PROGRESS_STATUSES = 'myProgressStatuses';
export const IM_PERMANENT_ACCEPTANCE = 'imPermanentAcceptance';
export const PROCESS_TYPE = 'processType';
export const AGENCY_INVOLVEMENT = 'agencyInvolvement';
export const APPOINTED_AS_RU = 'appointedAsRu';
export const INVOLVED_AGENCY_TRAFFIC_LIGHT_PAIRS = 'involvedAgencyTrafficLightPairs';
export const TRAIN_TYPES = 'trainTypes';
export const TRAIN_TYPE = 'trainType';
export const ADVANCED_SEARCH_FORM_TRAIN_TYPE_ID = `${ADVANCED_SEARCH_FORM_ID}.${TRAIN_TYPES}`;
export const ADVANCED_SEARCH_FORM_PROCESS_TYPE = `${ADVANCED_SEARCH_FORM_ID}.processType`;
export const ADVANCED_SEARCH_INVOLVED_AGENCY_TRAFFIC_LIGHT = `${ADVANCED_SEARCH_FORM_ID}.${INVOLVED_AGENCY_TRAFFIC_LIGHT_PAIRS}`;
export const ADVANCED_SEARCH_IM_PERMANENT_ACCEPTANCE = `${ADVANCED_SEARCH_FORM_ID}.${IM_PERMANENT_ACCEPTANCE}`;
export const ADVANCED_SEARCH_MY_PROGRESS_STATUSES = `${ADVANCED_SEARCH_FORM_ID}.${MY_PROGRESS_STATUSES}`;
export const ADVANCED_SEARCH_AGENCY_INVOLVEMENT = `${ADVANCED_SEARCH_FORM_ID}.${AGENCY_INVOLVEMENT}`;
export const ADVANCED_SEARCH_APPOINTED_AS_RU = `${ADVANCED_SEARCH_FORM_ID}.${APPOINTED_AS_RU}`;

export interface InvolvedAgencyTrafficLightForm {
  agency: Boxed<Agency>;
  progressStatus: Boxed<ProgressStatus>;
  imPermanentAcceptance: boolean | null;
}

export interface AdvancedSearchForm {
  dossierId: number;
  internationalTrainNumber: string;
  dossierName: string;
  dossierRoute: string;
  ttp: Boxed<TimetablePeriod>;
  processType: Boxed<ProcessType>;
  dossierStatuses: Boxed<DictionaryEntity[]>;
  dossierType: Boxed<DictionaryEntity>;
  phaseGroup: Boxed<DictionaryEntity>;
  cataloguePathNumber: string;
  trainNumber: string;
  trainTypes: Boxed<TrainType[]>;
  labels: Boxed<Label[]>;
  operationPoints: Boxed<OperationPoint[]>;
  rfcCorridor: Boxed<Corridor>;
  agencyInvolvement: Boxed<DictionaryEntity>;
  appointedAsRu: boolean | null;
  myProgressStatuses: Boxed<ProgressStatus[]>;
  imPermanentAcceptance: boolean | null;
  involvedAgencies: Boxed<Agency[]>;
  progressStatuses: Boxed<ProgressStatus[]>;
  involvedAgencyTrafficLightPairs: InvolvedAgencyTrafficLightForm[];
  transition: Boxed<any>;
  // required field for carry-forward search
  trainType: Boxed<TrainType>;
}

export interface State {
  advancedSearchForm: FormGroupState<AdvancedSearchForm>;
}

export const getTransitionValue = R.compose(unbox, getControlValue, safeProp('transition'), getControls);
export const getSelectedProcessTypeValue = R.compose(unbox, getControlValue, safeProp(PROCESS_TYPE), getControls);
export const getSelectedDossierStatusesValue = R.compose(unbox, getControlValue, safeProp('dossierStatuses'), getControls);
export const getSelectedMyProgressStatusesValue = R.compose(unbox, getControlValue, safeProp(MY_PROGRESS_STATUSES), getControls);
export const getInvolvedTrafficAgencyLightsValue = R.compose(unbox, getControlValue, safeProp(INVOLVED_AGENCY_TRAFFIC_LIGHT_PAIRS), getControls);
export const getSelectedTrainTypeValue = R.compose(unbox, getControlValue, safeProp(TRAIN_TYPE), getControls);
export const getAgencyInvolvementValue = R.compose(unbox, getControlValue, safeProp(AGENCY_INVOLVEMENT), getControls);
export const getImPermanentAcceptanceValue = R.compose(unbox, getControlValue, safeProp(IM_PERMANENT_ACCEPTANCE), getControls);

type BoxProperties = (dossierSearchCriteria: DossierSearchCriteria) => AdvancedSearchForm;
const boxProperties = <BoxProperties>R.evolve({
  ttp: box,
  processType: box,
  dossierStatuses: box,
  dossierType: box,
  phaseGroup: box,
  labels: box,
  operationPoints: box,
  rfcCorridor: box,
  myProgressStatuses: box,
  involvedAgencies: box,
  progressStatuses: box,
  involvedAgencyTrafficLightPairs: R.map(R.evolve({
    agency: box,
    progressStatus: box
  })),
  transition: box,
  trainTypes: box,
  agencyInvolvement: box,
  trainType: box
});

const initialFormValue: AdvancedSearchForm = {
  dossierId: null,
  internationalTrainNumber: null,
  dossierName: null,
  dossierRoute: null,
  ttp: null,
  processType: null,
  dossierStatuses: null,
  dossierType: null,
  phaseGroup: null,
  cataloguePathNumber: null,
  trainNumber: null,
  trainTypes: null,
  labels: null,
  operationPoints: null,
  rfcCorridor: null,
  agencyInvolvement: null,
  appointedAsRu: null,
  myProgressStatuses: null,
  imPermanentAcceptance: null,
  involvedAgencies: null,
  progressStatuses: null,
  involvedAgencyTrafficLightPairs: [],
  transition: null,
  trainType: null
};

const initialFormState: FormGroupState<AdvancedSearchForm> =
  createFormGroupState<AdvancedSearchForm>(ADVANCED_SEARCH_FORM_ID, initialFormValue);

export const initialState: State = {
  advancedSearchForm: initialFormState
};

const removeDossierStatusesControl = removeGroupControl<AdvancedSearchForm>('dossierStatuses');
const removeTransitionControl = removeGroupControl<AdvancedSearchForm>('transition');
const updateFormControls = R.cond([
  [R.equals(DossierSearchType.BULK_DOSSIER_PROMOTION), () => removeDossierStatusesControl],
  [R.T, () => removeTransitionControl]
]);

const initState = (state: State, {payload: {searchCriteria, searchType}}: InitAdvancedSearchForm) => {
  const completeSearchCriteria = boxProperties(
    R.mergeDeepRight(initialFormValue, {...searchCriteria}) as any
  );
  const formState = createFormGroupState<AdvancedSearchForm>(ADVANCED_SEARCH_FORM_ID, completeSearchCriteria);
  const updatedFormState = updateFormControls(searchType)(formState);
  return R.mergeRight(state, {
    advancedSearchForm: validateForm(setUserDefinedProperty(updatedFormState, 'searchType', searchType))
  });
};

const initStateReducer = createReducer<State, AdvancedSearchActions>([
  createHandler(AdvancedSearchActionTypes.InitAdvancedSearchForm, initState)
]);

const newInvolvedAgencyTrafficLightPair: InvolvedAgencyTrafficLightForm = ({
  agency: null,
  progressStatus: null,
  imPermanentAcceptance: null
});

const addField = (key, value) => updateGroup<AdvancedSearchForm>({
  [key]: setValue(value)
});

const addRequiredFields = (state: FormGroupState<AdvancedSearchForm>, {payload: requiredFields}): FormGroupState<AdvancedSearchForm> => {
  let updatedState = state;

  R.keys(requiredFields).forEach((key: string) => {
    const value = requiredFields[key];
    updatedState = addField(key, isObject(value) ? box(value) : value)(updatedState);
  });

  return updatedState;
};

const addNewInvolvedAgencyTrafficLightPair = (state: FormGroupState<AdvancedSearchForm>): FormGroupState<AdvancedSearchForm> => {
  const updatedForm = updateGroup<AdvancedSearchForm>(state, {
    involvedAgencyTrafficLightPairs: addArrayControl<InvolvedAgencyTrafficLightForm>(newInvolvedAgencyTrafficLightPair)
  });
  const indexOfAdded = R.length(updatedForm.controls.involvedAgencyTrafficLightPairs.controls) - 1;

  return updateGroup<AdvancedSearchForm>(updatedForm, {
    involvedAgencyTrafficLightPairs: updateArrayWithFilter(
      (fieldState, index) => index === indexOfAdded,
      updateGroup({
        imPermanentAcceptance: disable
      } as any))
  });
};

const removeInvolvedAgencyTrafficLightPair = (state: FormGroupState<AdvancedSearchForm>, {payload: index}): FormGroupState<AdvancedSearchForm> =>
  updateGroup<AdvancedSearchForm>(state, {
    involvedAgencyTrafficLightPairs: removeArrayControl(index)
  });

const resetField = (key: string) => (form: FormGroupState<AdvancedSearchForm>): FormGroupState<AdvancedSearchForm> =>
  updateGroup<AdvancedSearchForm>(form, {
    [key]: R.cond([
      [R.equals(INVOLVED_AGENCY_TRAFFIC_LIGHT_PAIRS), R.always(setValue([]))],
      [R.equals(IM_PERMANENT_ACCEPTANCE), R.always(R.compose(disable, setValue(null)))],
      [R.equals(APPOINTED_AS_RU), R.always(R.compose(disable, setValue(null)))],
      [R.T, R.always(setValue(null))]
    ])(key)
  });

const resetFormField = (form: FormGroupState<AdvancedSearchForm>, {payload: additionalFieldKey}): FormGroupState<AdvancedSearchForm> => R.cond([
  [R.equals(MY_PROGRESS_STATUSES), R.always(R.compose(resetField(MY_PROGRESS_STATUSES), resetField(IM_PERMANENT_ACCEPTANCE)))],
  [R.equals(AGENCY_INVOLVEMENT), R.always(R.compose(resetField(AGENCY_INVOLVEMENT), resetField(APPOINTED_AS_RU)))],
  [R.T, R.always(resetField(additionalFieldKey))]
])(additionalFieldKey)(form);

const resetAdditionalFields = (form: FormGroupState<AdvancedSearchForm>, {payload: dossierSearchType}): FormGroupState<AdvancedSearchForm> => {
  const additionalFieldsKeys = R.keys(R.pickBy(isTrue, getAdvancedSearchAdditionalFields(getControlValue(form), dossierSearchType)));
  let updatedForm = form;
  R.forEach((additionalFieldKey: string) =>
    updatedForm = resetFormField(updatedForm, {payload: additionalFieldKey}), additionalFieldsKeys);

  return updatedForm;
};

const resetFormState = (form: FormGroupState<AdvancedSearchForm>, {payload: formValue}) =>
  setValue(form, boxProperties(R.mergeDeepRight(initialFormValue, {
    ...formValue
  })));

const advancedSearchFormReducer = createReducer<FormGroupState<AdvancedSearchForm>, AdvancedSearchActions>([
  createHandler(AdvancedSearchActionTypes.ResetField, resetFormField),
  createHandler(AdvancedSearchActionTypes.ResetForm, resetFormState),
  createHandler(AdvancedSearchActionTypes.ResetAllAdditionalFields, resetAdditionalFields),
  createHandler(AdvancedSearchActionTypes.AddNewInvolvedAgencyTrafficLightPair, addNewInvolvedAgencyTrafficLightPair),
  createHandler(AdvancedSearchActionTypes.RemoveInvolvedAgencyTrafficLightPair, removeInvolvedAgencyTrafficLightPair),
  createHandler(AdvancedSearchActionTypes.AddRequiredFields, addRequiredFields)
]);

const validateFormForBulkTransition = updateGroup<AdvancedSearchForm>({
  ttp: validate(requiredValueValidator),
  transition: validate(requiredValueValidator)
});

const validateFormForCarryForwardAndBulkRuAppointment = updateGroup<AdvancedSearchForm>({
  ttp: validate(requiredValueValidator),
  dossierStatuses: validate(requiredValueValidator),
  trainType: validate(requiredValueValidator)
});

const validateFormForBulkAcceptanceIndicatorsAndBulkRuAppointmentProcessing = updateGroup<AdvancedSearchForm>({
  ttp: validate(requiredValueValidator),
  dossierStatuses: validate(requiredValueValidator)
});

const validateFormForPermanentAcceptance = updateGroup<AdvancedSearchForm>({
  ttp: validate(requiredValueValidator),
  processType: validate(requiredValueValidator),
  dossierStatuses: validate(requiredValueValidator),
  myProgressStatuses: validate(requiredValueValidator),
  imPermanentAcceptance: validate(requiredValueValidator)
});

const validateForm = (formState: FormGroupState<AdvancedSearchForm>) => {
  const {userDefinedProperties: {searchType}} = formState;

  return R.cond([
    [
      ofSearchType([DossierSearchType.BULK_ACCEPTANCE_INDICATOR_EDIT, DossierSearchType.BULK_RU_APPOINTMENT_PROCESSING]),
      R.always(validateFormForBulkAcceptanceIndicatorsAndBulkRuAppointmentProcessing(formState))
    ],
    [
      ofSearchType([DossierSearchType.CARRY_FORWARD, DossierSearchType.BULK_RU_APPOINTMENT]),
      R.always(validateFormForCarryForwardAndBulkRuAppointment(formState))
    ],
    [
      ofSearchType([DossierSearchType.BULK_PERMANENT_ACCEPTANCE]),
      R.always(validateFormForPermanentAcceptance(formState))
    ],
    [
      ofSearchType([DossierSearchType.BULK_DOSSIER_PROMOTION]),
      R.always(validateFormForBulkTransition(formState))
    ],
    [
      R.T,
      R.always(updateGroup<AdvancedSearchForm>(formState, {ttp: validate(requiredValueValidator)}))
    ]
  ])(searchType);
};

export const reducer = (state = initialState, action: Action): State => {
  const nextState = initStateReducer(state, <AdvancedSearchActions>action);

  const nextFormState = nextState.advancedSearchForm;

  const updatedFormState = executeReducers(nextFormState, action, [
    formStateReducer,
    advancedSearchFormReducer
  ]);

  return updatedFormState !== nextFormState ? {
    advancedSearchForm: validateForm(updatedFormState)
  } : nextState;
};
